import React, { useCallback, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import Tippy from "@tippyjs/react/headless";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import FocusTrap from "focus-trap-react";
import { AppContext } from "../../context";
import { InputWrapper } from "../InputWrapper";
import { renderReactChildrenWithData } from "../../utils";
import { BRANDS, MODES } from "../../configs";
import { PopoverInputAndIcon } from "./PopoverInputAndIcon";
import { FocusTrapChildren } from "./FocusTrapChildren";
import {
	FLIGHTS_CLASS_ID,
	FLIGHTS_PASSENGERS_ID,
	HOTELS_ROOM_AND_GUEST_FIELD_ID,
	PACKAGES_ROOM_AND_GUESTS_FIELD_ID
} from "../../configs/const";
import {
	isAndroid,
	isBrowser,
	isIOS,
	isIpad
} from "../../views/Shared/utils/detect-devices";
import styles from "./PopoverInput.scss";

const PopoverInput = ({
	id,
	mode,
	brand,
	label,
	value,
	defaultCloseIcon,
	isSOVMobile,
	isGuestMobile,
	className,
	icon,
	labelClassName,
	showToggleIcon,
	ParentRefElement,
	openRoomAndGuest,
	scrollRef,
	reactDOM,
	appRoot,
	onChange,
	onClose,
	setIsInitialSovMobileInputState,
	handleSovMobileInputSelectionToView,
	children,

	readOnly,
	popoverClassName
}) => {
	const [useAppState] = AppContext.useAppContext();
	let referenceElement = useRef(null);
	const [isReadOnlyReturnedFocus, setIsReadOnlyReturnedFocus] = useState(false);
	const [showPopover, setShowPopover] = useState(false);
	const [inputWidth, setInputWidth] = useState();
	const [containerWidth, setContainerWidth] = useState(appRoot.clientWidth);
	const isStackMobileLayout =
		useAppState.brand === BRANDS.SOV && !useAppState.sovSimplifiedSearchBar;
	const [
		externalMobileRoomAndGuestControl,
		setExternalMobileRoomAndGuestControl
	] = useState(false);

	const updateContainerSize = useCallback(() => {
		setContainerWidth(appRoot.clientWidth);
		setInputWidth(referenceElement?.current?.clientWidth ?? "100%");
	}, [appRoot, referenceElement]);

	useEffect(() => {
		if (ParentRefElement?.current && openRoomAndGuest && isSOVMobile) {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			referenceElement = ParentRefElement;
			setExternalMobileRoomAndGuestControl(true);
		} else {
			setExternalMobileRoomAndGuestControl(false);
		}
		window.addEventListener("resize", updateContainerSize);

		return () => {
			window.removeEventListener("resize", updateContainerSize);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (showPopover) {
			updateContainerSize();

			const targetElement =
				brand === BRANDS.SOV ? scrollRef?.current : referenceElement?.current;

			disableBodyScroll(targetElement, {
				reserveScrollBarGap: true,
				allowTouchMove: el => {
					let _el = el;
					while (_el && _el.parentElement) {
						if (_el.hasAttribute("body-scroll-lock-ignore")) {
							return true;
						}
						_el = _el.parentElement;
					}
					return false;
				}
			});
		}

		return () => {
			clearAllBodyScrollLocks();
		};
	}, [showPopover, appRoot, updateContainerSize, brand, scrollRef]);

	useEffect(() => {
		const handleDetectOutsideClick = e => {
			if (showPopover) {
				if (
					typeof e.composedPath === "function" &&
					!e.composedPath().includes(referenceElement?.current)
				) {
					e.stopPropagation();
					e.stopImmediatePropagation();
					e.preventDefault();

					if (onClose()) {
						setShowPopover(false);
					}
				}
			}
		};

		if (reactDOM !== document) {
			document.addEventListener("click", handleDetectOutsideClick);
		}

		return () => {
			if (reactDOM !== document) {
				document.removeEventListener("click", handleDetectOutsideClick);
			}
		};
	}, [reactDOM, referenceElement, showPopover, onClose]);

	const handleTogglePopover = handleOnFocus => {
		setIsReadOnlyReturnedFocus(true);
		setShowPopover(!showPopover);
		handleOnFocus();
	};

	const handleClick = handleOnFocus => {
		if (readOnly) {
			handleTogglePopover(handleOnFocus);

			if (brand === BRANDS.SOV) {
				setIsInitialSovMobileInputState(false);
				handleSovMobileInputSelectionToView(
					id,
					useAppState.getDictionary("who-is-coming")
				);
			}
		}

		window.WEB_CDN_BSB._emitter.emit("widgetToggle", true);
	};

	const handleKeyDown = (e, handleOnFocus) => {
		if (
			readOnly &&
			(e.key === "Enter" || e.key === " " || e.key === "Spacebar")
		) {
			e.preventDefault();
			handleTogglePopover(handleOnFocus);
		}
	};

	return (
		<InputWrapper
			ref={referenceElement}
			brand={brand}
			className={cx(className, styles.inputWrapper, {
				[styles[brand]]: styles[brand],
				[styles.popoverOpen]: showPopover,
				[styles.inputWrapperTabsView]: mode === MODES.TABS
			})}
			classNames={{
				label: labelClassName
			}}
			label={label}
			id={id}
			hasSelectedItem={!!value}
			isGuestField={true}
			isListOpen={showPopover}
			onBlur={() => !showPopover}>
			{({ isFocused, handleOnFocus, handleOnBlur }) => (
				<div
					className={cx(styles.inputContainer, {
						[styles[brand]]: styles[brand],
						[styles.inputContainerStackLayout]: isStackMobileLayout,
						[styles.focused]: isFocused,
						[styles.inputContainerIOSIPAD]: isIpad(),
						[styles.inputContainerIOSIPADFlights]:
							id === FLIGHTS_PASSENGERS_ID && isIpad()
					})}>
					<PopoverInputAndIcon
						brand={brand}
						styles={styles}
						value={value}
						icon={icon}
						tabIndex={brand === BRANDS.SOV ? "-1" : ""}
						readOnly={readOnly}
						onChange={onChange}
						showPopover={showPopover}
						showToggleIcon={showToggleIcon}
						isSOVMobile={useAppState.sovSimplifiedSearchBar}
						handleOnBlur={handleOnBlur}
						handleOnFocus={handleOnFocus}
						handleClick={() => handleClick(handleOnFocus)}
						handleKeyDown={() => handleKeyDown(handleOnFocus)}
						handleTogglePopover={() => handleTogglePopover(handleOnFocus)}
					/>
					{((externalMobileRoomAndGuestControl && brand === BRANDS.SOV) ||
						showPopover) && (
						<>
							{brand === BRANDS.SOV && (
								<div
									className={cx({
										[styles[brand]]: styles[brand],
										[styles.popoverTail]:
											(showPopover && !isSOVMobile) ||
											(showPopover && isGuestMobile) ||
											(showPopover && id === FLIGHTS_CLASS_ID && isBrowser()) ||
											(showPopover &&
												id === FLIGHTS_PASSENGERS_ID &&
												!isSOVMobile),
										[styles.popoverTailFlightIpadView]:
											mode === MODES.TABS && isIpad() && !isSOVMobile,
										[styles.popoverTailHotelsIpadView]:
											mode === MODES.TABS &&
											isIpad() &&
											!isSOVMobile &&
											(id === HOTELS_ROOM_AND_GUEST_FIELD_ID ||
												id === PACKAGES_ROOM_AND_GUESTS_FIELD_ID),

										[styles.popoverTailIos]:
											isIOS() &&
											isSOVMobile &&
											(id === FLIGHTS_PASSENGERS_ID || id === FLIGHTS_CLASS_ID),
										[styles.popoverTailChrome]:
											!isIOS() &&
											isBrowser() &&
											isSOVMobile &&
											(id === FLIGHTS_PASSENGERS_ID || id === FLIGHTS_CLASS_ID),
										[styles.popoverTailAndroid]:
											!isIOS() &&
											!isBrowser() &&
											isAndroid() &&
											isSOVMobile &&
											(id === FLIGHTS_PASSENGERS_ID || id === FLIGHTS_CLASS_ID),
										[styles.popoverTailStackLayout]: isStackMobileLayout
									})}
								/>
							)}
							<Tippy
								reference={referenceElement?.current}
								visible={externalMobileRoomAndGuestControl || showPopover}
								interactive={true}
								appendTo={referenceElement?.current}
								placement="bottom-start"
								popperOptions={{
									strategy: "fixed",
									modifiers: [
										{
											name: "flip",
											enabled: false
										},
										{
											name: "offset",
											enabled: true,
											options: {
												offset: [0, 8]
											}
										},
										{
											name: "preventOverflow",
											enabled: true,
											options: {
												padding: 0,
												boundary: appRoot
											}
										},
										{
											name: "maxHeight",
											enabled: true,
											phase: "afterMain",
											requiresIfExists: ["offset"],
											fn({ state }) {
												const height =
													window.innerHeight -
													16 -
													referenceElement?.current?.getBoundingClientRect()
														?.y -
													referenceElement?.current?.getBoundingClientRect()
														?.height;

												// eslint-disable-next-line no-param-reassign
												state.styles.popper = {
													...state.styles.popper,
													maxHeight: `${height}px`
												};
											}
										}
									]
								}}
								render={attrs => {
									const handleClosePopover = () => {
										if (onClose()) {
											setShowPopover(false);
											if (readOnly && isReadOnlyReturnedFocus) {
												setIsReadOnlyReturnedFocus(false);
												handleOnFocus(true);
											} else {
												handleOnBlur(true);
											}
											return true;
										}
										return false;
									};

									return (
										<FocusTrap
											active={showPopover}
											paused={!showPopover}
											dom={reactDOM}
											focusTrapOptions={{
												dom: reactDOM,
												allowOutsideClick: e => {
													const canClose = onClose();
													// If clicked on same input
													if (
														e.target
															.closest("label")
															?.contains(referenceElement?.current)
													) {
														return canClose;
													}

													if (!canClose) {
														e.stopPropagation();
													}

													return handleClosePopover();
												},
												onDeactivate: handleClosePopover
											}}>
											<FocusTrapChildren
												id={id}
												attrs={attrs}
												brand={brand}
												mode={mode}
												isSOVMobile={useAppState.sovSimplifiedSearchBar}
												showPopover={showPopover}
												defaultCloseIcon={defaultCloseIcon}
												popoverClassName={popoverClassName}
												styles={styles}
												isStackMobileLayout={isStackMobileLayout}
												inputWidth={inputWidth}
												containerWidth={containerWidth}
												handleClosePopover={handleClosePopover}
												onChange={onChange}>
												{renderReactChildrenWithData(children, {
													isOpen: showPopover,
													handleClose: handleClosePopover,
													handleUpdateValue: _value => {
														onChange(_value);
														handleClosePopover();
													}
												})}
											</FocusTrapChildren>
										</FocusTrap>
									);
								}}
							/>
						</>
					)}
				</div>
			)}
		</InputWrapper>
	);
};

PopoverInput.propTypes = {
	className: PropTypes.string,
	mode: PropTypes.oneOf(Object.keys(MODES)),
	brand: PropTypes.oneOf(Object.keys(BRANDS)),
	popoverClassName: PropTypes.string,
	labelClassName: PropTypes.string,
	id: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	icon: PropTypes.node,
	readOnly: PropTypes.bool,
	isSOVMobile: PropTypes.bool,
	isGuestMobile: PropTypes.bool,
	showToggleIcon: PropTypes.bool,
	openRoomAndGuest: PropTypes.bool,
	value: PropTypes.any.isRequired,
	children: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.node,
		PropTypes.arrayOf(PropTypes.node)
	]).isRequired,
	onChange: PropTypes.func.isRequired,
	onClose: PropTypes.func,
	setIsInitialSovMobileInputState: PropTypes.func,
	handleSovMobileInputSelectionToView: PropTypes.func,
	defaultCloseIcon: PropTypes.bool,
	ParentRefElement: PropTypes.shape({
		current: PropTypes.node
	}),
	scrollRef: PropTypes.shape({
		current: PropTypes.bool
	}),
	reactDOM: PropTypes.instanceOf(DocumentFragment),
	appRoot: PropTypes.any.isRequired
};

PopoverInput.defaultProps = {
	className: undefined,
	mode: MODES.SEARCH,
	brand: BRANDS.SWG,
	popoverClassName: undefined,
	labelClassName: undefined,
	icon: undefined,
	readOnly: true,
	showToggleIcon: false,
	openRoomAndGuest: false,
	isGuestMobile: false,
	isSOVMobile: false,
	onClose: () => true,
	setIsInitialSovMobileInputState: () => {},
	handleSovMobileInputSelectionToView: () => {},
	defaultCloseIcon: true,
	ParentRefElement: {
		current: null
	},
	reactDOM: document,
	scrollRef: {
		current: null
	}
};

export default PopoverInput;
export { PopoverInput };
